import { useState, useEffect, useCallback } from "react";
import { query, getDocs, collection, doc, getDoc, orderBy } from "firebase/firestore";
import { auth, db } from '../../config/firebase';
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight} from '@fortawesome/free-solid-svg-icons';
import {faExclamationTriangle,faExclamationCircle,faChevronDown,faChevronUp,faMapMarkerAlt,faTools,faBuilding,faHotel,faClock,faDownload,faRefresh} from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx";
import "../../styles/Incidents.css";

const SuperAdminIncidents = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, authLoading, authError] = useAuthState(auth);
  const navigate = useNavigate();
  
  // State for all tenants and their incidents
  const [tenantsIncidents, setTenantsIncidents] = useState([]);
  const [expandedTenant, setExpandedTenant] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSeverity, setSelectedSeverity] = useState("all");
  const [expandedDates, setExpandedDates] = useState({});
  const today = new Date().toISOString().split("T")[0];
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenants, setTenants] = useState([]);
  const [TenantCard, setTenantCard] = useState([]);

  // Check if device is overdue
  const isDeviceOverdue = (lastSubmissionDate, checkFrequency) => {
    if (!lastSubmissionDate || !checkFrequency) return false;
    
    const lastDate = new Date(lastSubmissionDate);
    const todayDate = new Date(today);
    const daysDifference = Math.floor((todayDate - lastDate) / (1000 * 60 * 60 * 24));
    
    return daysDifference > checkFrequency;
  };

  // Validate super admin access
  const validateSuperAdminAccess = useCallback(async () => {
    if (!user) return false;

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (!userDoc.exists()) {
        setError("User not found");
        return false;
      }

      const userData = userDoc.data();
      const userRole = userData.role;

      // Check if user is super-admin
      if (userRole !== 'super-admin') {
        setError("Access denied: Super admin privileges required");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error validating super admin access:", error);
      setError(error.message);
      return false;
    }
  }, [user]);


useEffect(() => {
  const fetchTenants = async () => {
    const snap = await getDocs(collection(db, 'tenants'));
    const tenantsList = snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setTenants(tenantsList);
  };

  fetchTenants();
}, []);


// First, let's create a helper function to group incidents by date
const groupIncidentsByDate = (incidents) => {
  const grouped = {};
  
  incidents.forEach(incident => {
    const dateKey = incident.timestamp?.toISOString().split('T')[0] || 'no-date';
    
    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: incident.timestamp || new Date(),
        incidents: []
      };
    }
    
    grouped[dateKey].incidents.push(incident);
  });
  
  // Sort dates in descending order (newest first)
  return Object.values(grouped).sort((a, b) => b.date - a.date);
};

// Fetch incidents for a specific tenant with date filtering
const fetchTenantIncidents = async (tenantId, tenantName, selectedDate = null) => {
  try {
    console.log("Fetching incidents for tenant:", tenantId, tenantName, "for date:", selectedDate);

    // Fetch all locations for this tenant
    const locationsQuery = collection(db, `tenants/${tenantId}/locations`);
    const locationsSnapshot = await getDocs(locationsQuery);

    if (locationsSnapshot.empty) {
      console.log("No locations found for tenant:", tenantId);
      return {
        tenantId,
        tenantName,
        incidents: [],
        totalIncidents: 0,
        highSeverityCount: 0,
        mediumSeverityCount: 0
      };
    }

    const locationsData = {};
    const allIncidents = [];

    // Get location names
    locationsSnapshot.docs.forEach(locDoc => {
      locationsData[locDoc.id] = locDoc.data().name || locDoc.id;
    });

    // Process each location's rooms
    for (const locationDoc of locationsSnapshot.docs) {
      const locationId = locationDoc.id;
      const locationName = locationsData[locationId];

      try {
        const roomsQuery = collection(db, `tenants/${tenantId}/locations/${locationId}/rooms`);
        const roomsSnapshot = await getDocs(roomsQuery);

        for (const roomDoc of roomsSnapshot.docs) {
          const roomId = roomDoc.id;
          const roomData = roomDoc.data();

          // Check room infrastructure status - only include if it matches the selected date
          if (roomData.infraStatus && 
              ['NOT_READY', 'WARNING', 'CRITICAL'].includes(roomData.infraStatus)) {
            const incidentDate = roomData.lastUpdated?.toDate();
            if (!selectedDate || isSameDay(incidentDate, selectedDate)) {
              allIncidents.push({
                id: `room-${locationId}-${roomId}`,
                type: 'room',
                category: 'Room Issue',
                locationId,
                locationName,
                roomId,
                roomName: roomData.name || roomId,
                infraStatus: roomData.infraStatus,
                infraComment: roomData.infraComment || "No comments",
                lastUpdated: incidentDate,
                severity: roomData.infraStatus === 'CRITICAL' ? 'high' : 
                         roomData.infraStatus === 'NOT-READY' ? 'high' : 'medium',
                floor: roomData.floor || "N/A",
                checkFrequency: roomData.checkFrequency || 1,
                timestamp: incidentDate || new Date(),
                comment: roomData.infraComment || "No comments"
              });
            }
          }

          // Get submissions to check device status
          try {
            const submissionsQuery = query(
              collection(
                db,
                `tenants/${tenantId}/locations/${locationId}/rooms/${roomId}/submissions`
              ),
              orderBy("timestamp", "desc")
            );

            const submissionsSnapshot = await getDocs(submissionsQuery);

            if (!submissionsSnapshot.empty) {
              for (const submissionDoc of submissionsSnapshot.docs) {
                const submissionData = submissionDoc.data();
                const submissionDate = submissionData.date;
                const submissionTimestamp = submissionData.timestamp?.toDate();

                // Skip if we have a selected date and this submission doesn't match it
                if (selectedDate && !isSameDay(submissionTimestamp, selectedDate)) {
                  continue;
                }

                if (submissionData.devices?.length) {
                  submissionData.devices.forEach((device) => {
                    const deviceKey = `${locationId}-${roomId}-${device.id}`;
                    
                    // Check if device is not working
                    if (device.status === 'NOT-WORKING') {
                      allIncidents.push({
                        id: deviceKey,
                        type: 'device',
                        category: 'Not Working Device',
                        locationId,
                        locationName,
                        roomId,
                        roomName: roomData.name || roomId,
                        deviceName: device.name || device.id,
                        status: device.status,
                        comment: device.comment || "No comments",
                        timestamp: submissionTimestamp || new Date(),
                        submissionDate,
                        severity: 'high',
                        checkFrequency: roomData.checkFrequency || 1
                      });
                    }

                    // Check if device is overdue
                    const isOverdue = isDeviceOverdue(submissionDate, roomData.checkFrequency || 1);
                    if (isOverdue && device.status !== 'NOT-WORKING') {
                      const daysSinceLastCheck = Math.floor((new Date() - new Date(submissionDate)) / (1000 * 60 * 60 * 24));
                      allIncidents.push({
                        id: `overdue-${deviceKey}`,
                        type: 'device',
                        category: 'Overdue Device',
                        locationId,
                        locationName,
                        roomId,
                        roomName: roomData.name || roomId,
                        deviceName: device.name || device.id,
                        status: device.status,
                        comment: `Device overdue for check (${daysSinceLastCheck} days)`,
                        timestamp: submissionTimestamp || new Date(),
                        submissionDate,
                        severity: 'medium',
                        isOverdue: true,
                        checkFrequency: roomData.checkFrequency || 1,
                        daysSinceLastCheck
                      });
                    }
                  });
                }
              }
            }
          } catch (submissionError) {
            console.error(`Error fetching submissions for room ${roomId}:`, submissionError);
          }
        }
      } catch (roomError) {
        console.error(`Error processing location ${locationId}:`, roomError);
      }
    }

    // Sort incidents by severity and timestamp
    allIncidents.sort((a, b) => {
      const severityOrder = { high: 3, medium: 2, low: 1 };
      if (severityOrder[a.severity] !== severityOrder[b.severity]) {
        return severityOrder[b.severity] - severityOrder[a.severity];
      }
      return new Date(b.timestamp || 0) - new Date(a.timestamp || 0);
    });

    const highSeverityCount = allIncidents.filter(i => i.severity === 'high').length;
    const mediumSeverityCount = allIncidents.filter(i => i.severity === 'medium').length;

    return {
      tenantId,
      tenantName,
      incidents: allIncidents,
      totalIncidents: allIncidents.length,
      highSeverityCount,
      mediumSeverityCount
    };

  } catch (error) {
    console.error(`Error fetching incidents for tenant ${tenantId}:`, error);
    return {
      tenantId,
      tenantName,
      incidents: [],
      totalIncidents: 0,
      highSeverityCount: 0,
      mediumSeverityCount: 0,
      error: error.message
    };
  }
};

// Helper function to check if two dates are the same day
function isSameDay(date1, date2) {
  if (!date1 || !date2) return false;
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

// Fetch all tenants and their incidents with date filtering
const fetchAllTenantsIncidents = useCallback(async (selectedDate = null) => {
  try {
    setLoading(true);
    setError(null);

    // Validate super admin access first
    const hasAccess = await validateSuperAdminAccess();
    if (!hasAccess) {
      setLoading(false);
      return;
    }

    // Fetch all tenants
    const tenantsQuery = collection(db, "tenants");
    const tenantsSnapshot = await getDocs(tenantsQuery);

    if (tenantsSnapshot.empty) {
      console.log("No tenants found");
      setTenantsIncidents([]);
      setLoading(false);
      return;
    }

    console.log("Found tenants:", tenantsSnapshot.size);

    // Fetch incidents for each tenant
    const tenantsWithIncidents = [];
    
    for (const tenantDoc of tenantsSnapshot.docs) {
      const tenantData = tenantDoc.data();
      const tenantIncidents = await fetchTenantIncidents(tenantDoc.id, tenantData.name, selectedDate);
      
      // ONLY ADD TENANTS THAT HAVE INCIDENTS
      if (tenantIncidents.totalIncidents > 0) {
        tenantsWithIncidents.push(tenantIncidents);
      } else {
        console.log(`Skipping tenant ${tenantData.name} - no incidents found`);
      }
    }

    // Sort tenants by total incidents (descending)
    tenantsWithIncidents.sort((a, b) => b.totalIncidents - a.totalIncidents);

    console.log(`Found ${tenantsWithIncidents.length} tenants with incidents out of ${tenantsSnapshot.size} total tenants`);
    setTenantsIncidents(tenantsWithIncidents);

  } catch (error) {
    console.error("Error fetching all tenants incidents:", error);
    setError(`Error fetching incidents: ${error.message}`);
  } finally {
    setLoading(false);
  }
}, [validateSuperAdminAccess]);

const filteredTenants = tenantsIncidents.filter((tenant) => {
  const matchesSearch =
    searchQuery === "" ||
    tenant.tenantName.toLowerCase().includes(searchQuery.toLowerCase());

  const matchesSeverity =
    selectedSeverity === "all" ||
    (selectedSeverity === "high" && tenant.highSeverityCount > 0) ||
    (selectedSeverity === "medium" && tenant.mediumSeverityCount > 0) ||
    (selectedSeverity === "none" && tenant.totalIncidents === 0);

  const matchesTenantId =
    selectedTenantId === "" || tenant.tenantId === selectedTenantId; // ✅ Fix here

  return matchesSearch && matchesSeverity && matchesTenantId;
});

  // Download all incidents report
  const downloadAllIncidentsReport = () => {
    const allIncidents = [];
    
    tenantsIncidents.forEach(tenant => {
      tenant.incidents.forEach((incident, index) => {
        allIncidents.push({
          "S.No": allIncidents.length + 1,
          "Tenant": tenant.tenantName,
          "Category": incident.category,
          "Severity": incident.severity.toUpperCase(),
          "Location": incident.locationName,
          "Room": incident.roomName,
          "Device": incident.deviceName || "N/A",
          "Status": incident.type === 'room' ? incident.infraStatus : incident.status,
          "Comment": incident.comment || "No comments",
          "Last Updated": incident.timestamp?.toLocaleString() || "N/A"
        });
      });
    });

    if (allIncidents.length === 0) return;

    const worksheet = XLSX.utils.json_to_sheet(allIncidents);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "All Tenants Incidents");

    const fileName = `All_Tenants_Incidents_Report_${today}`;
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  // Toggle tenant expansion
  const toggleTenantExpansion = (tenantId) => {
    setExpandedTenant(expandedTenant === tenantId ? null : tenantId);
  };

  // Handle authentication and initial load
  useEffect(() => {
    if (authLoading) return;
    
    if (authError) {
      setError("Authentication error");
      return;
    }

    if (!user) {
      navigate('/login');
      return;
    }

    fetchAllTenantsIncidents();
  }, [user, authLoading, authError, navigate, fetchAllTenantsIncidents]);

  const handleGoBack = () => {
    navigate('/admin'); // Navigate back to admin panel
  };

  if (authLoading || loading) return ;
  
  if (authError || error) {
    return (
      <div className="error-message">
        <FontAwesomeIcon icon={faExclamationTriangle} />
        <h3>Error</h3>
        <p>{error || authError?.message}</p>
        <button onClick={() => navigate('/admin')} className="btn-primary">
          Go Back to Admin Panel
        </button>
      </div>
    );
  }
  
  

  const totalIncidentsAllTenants = tenantsIncidents.reduce((sum, tenant) => sum + tenant.totalIncidents, 0);
  const totalHighSeverity = tenantsIncidents.reduce((sum, tenant) => sum + tenant.highSeverityCount, 0);
  const totalMediumSeverity = tenantsIncidents.reduce((sum, tenant) => sum + tenant.mediumSeverityCount, 0);
  const tenantsWithIncidents = tenantsIncidents.filter(tenant => tenant.totalIncidents > 0).length;

  return (
    <div className="incidents-container">
      <div className="incidents-header">
        <div className="header-left">
          <button className="backbutton" onClick={handleGoBack}>
            ← Back
          </button>
          <h1>All Tenants Incidents Overview</h1>
        </div>
        
        <div className="header-actions-incident">
          <div className="incidents-summary">
            <span className="total-incidents">
              Total: {totalIncidentsAllTenants}
            </span>
            <span className="high-severity">
              High: {totalHighSeverity}
            </span>
            <span className="medium-severity">
              Medium: {totalMediumSeverity}
            </span>
            <span className="tenants-affected">
              Affected Tenants: {tenantsWithIncidents}
            </span>
          </div>
          
          {/* <button 
            onClick={fetchAllTenantsIncidents}
            className="refresh-button"
            title="Refresh all incidents"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button> */}
          
          <button 
            onClick={downloadAllIncidentsReport}
            className="download-button"
            disabled={totalIncidentsAllTenants === 0}
            title="Export all incidents report"
          >
            <FontAwesomeIcon icon={faDownload} />
            Export All
          </button>
        </div>
      </div>

<div className="incidents-filters">
  <input
    type="text"
    placeholder="Search tenants..."
    className="search-input"
    value={searchQuery}
    onChange={(e) => setSearchQuery(e.target.value)}
  />


  <select
    className="filter-select"
    value={selectedSeverity}
    onChange={(e) => setSelectedSeverity(e.target.value)}
  >
    <option value="all">All Tenants</option>
    <option value="high">Tenants with High Issues</option>
    <option value="medium">Tenants with Medium Issues</option>
  </select>

  {/* 🏢 Tenant Filter Dropdown */}
<select
className="filter-select"
  value={selectedTenantId}
  onChange={(e) => setSelectedTenantId(e.target.value)}
>
  <option value="">All Tenants</option>
  {tenants.map(tenant => (
    <option key={tenant.id} value={tenant.id}>
      {tenant.name}
    </option>
  ))}
</select>

</div>


      <div className="tenants-list">
        {filteredTenants.length === 0 ? (
          <div className="no-incidents">
            <FontAwesomeIcon icon={faExclamationCircle} />
            <h3>No tenants found</h3>
            <p>No tenants match your current filters</p>
          </div>
        ) : (
          filteredTenants.map(tenant => (
            <div key={tenant.tenantId} className="tenant-card">
              
<div 
  className={`tenant-header ${expandedTenant === tenant.tenantId ? 'active' : ''}`}
  onClick={() => toggleTenantExpansion(tenant.tenantId)}
>
                <div className="tenant-info">
                  <h3>{tenant.tenantName}</h3>
                </div>
                
                   <div className="tenant-stats">
                            <span className="total-count">
                            Total Incidents: {tenant.totalIncidents}
                            </span>
                            {tenant.highSeverityCount > 0 && (
                            <span className="high-severity">
                               High : {tenant.highSeverityCount}
                            </span>
                            )}
                            {tenant.mediumSeverityCount > 0 && (
                            <span className="medium-severity">
                                Medium: {tenant.mediumSeverityCount}
                            </span>
                            )}
                            {tenant.totalIncidents === 0 && (
                            <span className="no-incidents-badge">No Issues</span>
                            )}
                        <div className="expand-icon">
                        <FontAwesomeIcon 
                            icon={expandedTenant === tenant.tenantId ? faChevronUp : faChevronDown} 
                        />
                        </div>
                    </div>
                
              </div>

{expandedTenant === tenant.tenantId && tenant.totalIncidents > 0 && (
  <div className="tenant-incidents">
    {/* Group incidents by date */}
    {Object.entries(
      tenant.incidents.reduce((acc, incident) => {
        const dateKey = incident.timestamp 
          ? incident.timestamp.toISOString().split('T')[0] 
          : 'no-date';
        
        if (!acc[dateKey]) {
          acc[dateKey] = {
            date: incident.timestamp || new Date(),
            incidents: []
          };
        }
        acc[dateKey].incidents.push(incident);
        return acc;
      }, {})
    )
    .sort(([a], [b]) => new Date(b) - new Date(a)) // Sort dates newest first
    .map(([dateKey, dateGroup]) => {
      const dateStr = dateGroup.date.toLocaleDateString(undefined, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      
      return (
        <div key={dateKey} className="date-group">
          <div 
            className="date-header"
            onClick={() => {
              // Toggle expanded state for this date
              const newExpandedDates = {...expandedDates};
              newExpandedDates[dateKey] = !expandedDates[dateKey];
              setExpandedDates(newExpandedDates);
            }}
          >
            <FontAwesomeIcon 
              icon={expandedDates[dateKey] ? faChevronDown : faChevronRight} 
            />
            <span className="date-title">{dateStr}</span>
            <span className="incident-count-badge">
              {dateGroup.incidents.length} incident{dateGroup.incidents.length !== 1 ? 's' : ''}
            </span>
            <span className="severity-summary">
              {dateGroup.incidents.filter(i => i.severity === 'high').length > 0 && (
                <span className="high-count">
                  {dateGroup.incidents.filter(i => i.severity === 'high').length} High
                </span>
              )}
              {dateGroup.incidents.filter(i => i.severity === 'medium').length > 0 && (
                <span className="medium-count">
                  {dateGroup.incidents.filter(i => i.severity === 'medium').length} Medium
                </span>
              )}
            </span>
          </div>

          {expandedDates[dateKey] && (
            <div className="date-incidents-container">
              {dateGroup.incidents
                .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Sort incidents newest first
                .map(incident => (
                  <div 
                    key={incident.id}
                    className={`incident-card ${incident.severity}`}
                  >
                    <div className="incident-header">
                      <div className="incident-type">
                        <FontAwesomeIcon 
                          icon={incident.type === 'room' ? faBuilding : faTools} 
                        />
                        <span className="category">{incident.category}</span>
                        <span className={`severity-badge ${incident.severity}`}>
                          {incident.severity.toUpperCase()}
                        </span>
                      </div>
                      <div className="incident-time">
                        <FontAwesomeIcon icon={faClock} />
                        {incident.timestamp?.toLocaleTimeString(undefined, {
                          hour: '2-digit',
                          minute: '2-digit'
                        }) || "N/A"}
                      </div>
                    </div>

                    <div className="incident-details">
                      <div className="location-info">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                        <span>Location: {incident.locationName}</span>
                        {incident.floor && incident.floor !== "N/A" && (
                          <span className="floor">Floor {incident.floor}</span>
                        )}
                      </div>

                      <div className="location-info">
                        <FontAwesomeIcon icon={faHotel} />
                        <span>Room: {incident.roomName}</span>
                      </div>

                      {incident.deviceName && (
                        <div className="device-info">
                          <FontAwesomeIcon icon={faTools} />
                          <span>{incident.deviceName}</span>
                        </div>
                      )}

                      <div className="status-info">
                        <span className={`status ${(incident.infraStatus || incident.status || '').toLowerCase().replace('-', '')}`}>
                          {incident.infraStatus || incident.status?.replace('-', ' ')}
                        </span>
                      </div>

                      <div className="comment">
                        <p>{incident.comment}</p>
                      </div>

                      {incident.isOverdue && (
                        <div className="overdue-warning">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          <span>
                            Overdue by {incident.daysSinceLastCheck} days 
                            (Check frequency: {incident.checkFrequency} days)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      );
    })}
  </div>
)}

              {expandedTenant === tenant.tenantId && tenant.totalIncidents === 0 && (
                <div className="no-incidents-expanded">
                  <FontAwesomeIcon icon={faExclamationCircle} />
                  <p>All systems are running normally for this tenant</p>
                </div>
              )}

              {tenant.error && (
                <div className="tenant-error">
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <p>Error loading incidents: {tenant.error}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SuperAdminIncidents;